import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Select, Spin, Table, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxiosClient from "../../../axios-client.js";

/**
 * The users in a role.
 *
 * Replaces an antd Transfer, which was the wrong shape for the task: a Transfer
 * is symmetric — two equal lists, two directions — but one side here is the
 * handful of people you care about and the other is every user in the business,
 * which is why the old screen loaded them all unpaged. It also could not show
 * anything about a user beyond a label, so "does this person already have
 * Admin?" had no answer.
 *
 * Instead: a Table of the members with their other roles, and a Select with
 * server-side search for adding, so paging stays the API's job.
 */

const PROTECTED_ROLE = 'SUDO';

function AssignUserRoles({ role, onMembershipChange }) {
  const axiosClient = useAxiosClient();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/users/getRoleUsers?role=${encodeURIComponent(role)}`);
      setMembers(response.data);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, role]);

  useEffect(() => { load(); }, [load]);

  /** Server-side search, so the whole directory never has to come down. */
  const handleSearch = useCallback((query) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!query || query.trim().length < 2) {
      setOptions([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axiosClient.get(`/users/index?search=${encodeURIComponent(query.trim())}`);
        const rows = response.data?.data ?? response.data ?? [];
        const memberIds = new Set(members.map((m) => m.id));
        setOptions(
          rows
            .filter((u) => !memberIds.has(u.id))
            .map((u) => ({
              value: u.id,
              label: `${[u.firstname, u.lastname].filter(Boolean).join(' ') || u.name} · ${u.email}`,
            })),
        );
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message)
          || error.message || error.toString();
        toast.error(message);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, [axiosClient, members]);

  const add = async (userId) => {
    setOptions([]);
    try {
      await axiosClient.post('/roles/assignRole', { userIds: [userId], role });
      toast.success(`Added to ${role}.`);
      load();
      // The role list and the tab badge both show a user count.
      onMembershipChange?.();
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    }
  };

  const remove = async (user) => {
    setBusyId(user.id);
    try {
      await axiosClient.post('/roles/removeRole', { userIds: [user.id], role });
      toast.success(`${user.name} removed from ${role}.`);
      load();
      onMembershipChange?.();
    } catch (error) {
      // 422 here is the last-holder invariant, refused server-side.
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  // Blocked in the row rather than after the request, though the API refuses it
  // either way — see AccessInvariants::canRemoveUsersFromRole.
  const isLastProtectedHolder = role === PROTECTED_ROLE && members.length <= 1;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, record) => <Link to={`/users/${record.id}`}>{name}</Link>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (email) => <span style={{ color: 'var(--muted)' }}>{email || '—'}</span>,
    },
    {
      title: 'Other roles',
      dataIndex: 'roles',
      render: (roles) => {
        const others = (roles || []).filter((r) => r !== role);
        return others.length
          ? others.map((r) => <Tag key={r} style={{ marginInlineEnd: 4 }}>{r}</Tag>)
          : <span style={{ color: 'var(--dim)' }}>—</span>;
      },
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 110,
      render: (_, record) => (
        <Button
          size="small"
          danger
          type="text"
          loading={busyId === record.id}
          disabled={isLastProtectedHolder}
          title={isLastProtectedHolder
            ? `${PROTECTED_ROLE} must keep at least one user — assign a replacement first.`
            : undefined}
          onClick={() => remove(record)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '14px 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Select
        showSearch
        value={null}
        placeholder={`Add a user to ${role} — search by name or email`}
        filterOption={false}
        onSearch={handleSearch}
        onSelect={add}
        options={options}
        notFoundContent={searching ? <Spin size="small" /> : null}
        style={{ width: '100%', maxWidth: 460 }}
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={members}
        pagination={members.length > 10 ? { pageSize: 10, size: 'small' } : false}
        locale={{ emptyText: `No users in ${role} yet.` }}
      />

      <div style={{ font: '400 11.5px/1.5 Outfit, sans-serif', color: 'var(--dim)' }}>
        Adding or removing a user takes effect immediately; it is not part of the permission save.
        {role === PROTECTED_ROLE && ` The last ${PROTECTED_ROLE} holder cannot be removed — assign a replacement first.`}
      </div>
    </div>
  );
}

export default AssignUserRoles;
